import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { Lane } from '../../../interfaces/services/lanes';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the ShipperDetails Lanes Map Source. */
export interface ShipperHealthLanesMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    color: string;
    strokeColor?: string;
    description: string;
}

/**
 * Loop through the lanes and add a point feature to the source for each lane origin and destination.
 * Loop through the lanes and add a line feature to the source for each lane.
 * Adds the shipper health lanes source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param lanes The lanes to show on the map
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addShipperHealthLanesSource = (map: TrimbleMaps.Map, lanes: Lane[], theme: Theme): GeoJSONSourceRaw => {
    const features: GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, ShipperHealthLanesMapSourceProperties>[] = [];

    lanes.forEach((lane): void => {
        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value &&
            lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const laneTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Origin:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Destination:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                        [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                    ],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.ShipperLaneLine,
                    color: theme.palette.secondary.main,
                    description: laneTooltipHtml,
                },
            });
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value) {
            const pickupAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.pickup_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                },
                properties: {
                    id: lane.pickup_service_area_id,
                    featureType: FeatureTypes.ShipperLaneCenter,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    description: pickupAreaTooltipHtml,
                },
            });
        }

        if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const deliveryAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.delivery_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.ShipperLaneCenter,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    description: deliveryAreaTooltipHtml,
                },
            });
        }
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
    };

    if (map.getSource(MapSources.shipperHealthLanesSource) === undefined) {
        map.addSource(MapSources.shipperHealthLanesSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.shipperHealthLanesSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
